/* eslint-disable react-refresh/only-export-components */
/**
 * Left sidebar — the practice apps' port of the in-game (platform-2.0)
 * SidebarOpen drawer (client 2026-10-07): a small rounded dark panel,
 * 264px wide and only as tall as its content (scrolls when the screen is
 * shorter), with its sigma exactly on top of the header bar's sigma — so it
 * follows the app's centred box at any window size, not the window's edge.
 * The app switcher is the in-game one too (AppSwitcherRail, client
 * 2026-11-03): the arrows open the other apps, with their tiles, in the
 * drawer itself; the app's own actions follow,
 * built from SidebarItem / SidebarDivider / HelpAndSupportItem /
 * CheckForUpdatesItem in the in-game order and look. The drawer closes by
 * tapping outside or navigating — no X button.
 *
 * The apps listed are the ones the coach is subscribed to (in-game lists all
 * four, the others dimmed). One opens by the same rule as the practice
 * dashboard tiles: online, any; offline, only one that is ready — service
 * worker controlling the page AND `persist:<app>-root` present (opened
 * online at least once).
 */
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type Ref,
  type KeyboardEvent,
  type TouchEvent,
} from "react";
import { createPortal } from "react-dom";
import { ChevronsUpDown, CircleHelp, RefreshCw } from "lucide-react";
import { authServices, selectedAppStore } from "@workspace/shared";
import { APP_ICONS, Logo } from "@workspace/assets";
import { cn } from "../../lib/utils.ts";
import { wasTouchTap } from "../../lib/touch.ts";
import { PendingUpdateDot } from "../app-update/PendingUpdateDot.tsx";
import { openCheckForUpdates } from "../app-update/CheckForUpdatesDialog.tsx";
import { HelpMenu } from "../support/HelpMenu.tsx";
import { openSupport } from "../support/supportCenter.ts";

export type PracticeAppOption = { id: string; name: string };

/** All 12 practice apps, in dashboard order (offense, defense, st, practice). */
const PRACTICE_APPS: PracticeAppOption[] = [
  { id: "qb", name: "Quarterback" },
  { id: "rb", name: "Running Back" },
  { id: "te", name: "Tight End" },
  { id: "ol", name: "Offensive Line" },
  { id: "wr", name: "Wide Receiver" },
  { id: "lb", name: "Linebacker" },
  { id: "s", name: "Safety" },
  { id: "dl", name: "Defensive Line" },
  { id: "db", name: "Defensive Back" },
  { id: "dc", name: "Defensive Coordinator" },
  { id: "st", name: "Special Teams" },
  { id: "def", name: "Practice" },
];

/** Canonical per-app key the backend buckets by (must match the dashboard's
 *  APP_KEY_BASE_PATHS / the sweep's APP_SELECTED_KEY). */
const APP_KEY_BY_ID: Record<string, string> = {
  qb: "QB",
  st: "ST",
  wr: "WideReceiver",
  db: "DefensiveBack",
  dc: "DefensiveCoordinator",
  dl: "DL",
  lb: "LineBacker",
  rb: "RunningBack",
  s: "Safety",
  te: "TightEnd",
  ol: "OL",
  def: "DefLineMan",
};

const readCachedSubscriptions = (): string[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem("cachedSubscriptions") || "[]");
    return Array.isArray(parsed) ? parsed.map((s) => String(s).toLowerCase()) : [];
  } catch {
    return [];
  }
};

/** Same rule as the dashboard tiles' offline gate (lib/offlineReady). */
const isOfflineReady = (appId: string): boolean => {
  try {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
    if (navigator.serviceWorker.controller == null) return false;
    return localStorage.getItem(`persist:${appId}-root`) != null;
  } catch {
    return false;
  }
};

/** The apps the coach's subscriptions cover ("team" covers all), in dashboard order. */
export const getSubscribedPracticeApps = (): PracticeAppOption[] => {
  const subs = readCachedSubscriptions();
  return PRACTICE_APPS.filter((app) => subs.includes("team") || subs.includes(app.id));
};

/**
 * App-switcher options for the current session. Online: every app the coach's
 * subscriptions cover ("team" covers all). Offline: only offline-ready apps.
 */
export const getPracticeAppOptions = (isOnline: boolean): PracticeAppOption[] => {
  const subscribed = getSubscribedPracticeApps();
  return isOnline ? subscribed : subscribed.filter((app) => isOfflineReady(app.id));
};

/**
 * Switch to another practice app. Mirrors the dashboard hand-off: release
 * this device's session rows first (frees the current app's slot for other
 * devices, exactly like returning to the dashboard would), stamp the target
 * app key, then do a full navigation so the target app boots and registers
 * its own session.
 */
/** The release is a courtesy (it frees the old app's session slot early); the
 *  target app re-registers regardless, so it must never delay the switch. */
const RELEASE_TIMEOUT_MS = 3000;

export const switchPracticeApp = async (appId: string): Promise<void> => {
  if (typeof navigator === "undefined" || navigator.onLine) {
    try {
      // BOUNDED: an offline/flaky request used to hang here forever and the
      // app switch never happened.
      await Promise.race([
        authServices.onReleaseDeviceSessions(),
        new Promise((resolve) => setTimeout(resolve, RELEASE_TIMEOUT_MS)),
      ]);
    } catch {
      /* best effort — the target app's register will still reconcile */
    }
  }
  try {
    selectedAppStore
      .getState()
      .setSelectedAppState({ key: "appKey", value: APP_KEY_BY_ID[appId] ?? "Dashboard" });
  } catch {
    /* store not initialized — the target app sets it on boot anyway */
  }
  window.location.href = `/${appId}/play-entry`;
};

/** A row of the other apps' list: a 20px icon with py-1.5, 4px apart. */
const RAIL_ROW_PX = 32;
const RAIL_GAP_PX = 4;
/** The list's room under the header: five rows, about the drawer's old gap
 *  there (230px). A longer list opens over the menu below it. */
const RAIL_RESERVED_ROWS = 5;

const rowsHeight = (rows: number) => (rows > 0 ? rows * RAIL_ROW_PX + (rows - 1) * RAIL_GAP_PX : 0);

/** The room the other apps' list takes in the drawer, open or closed. */
export function railReservedHeight(count: number): number {
  return rowsHeight(Math.min(count, RAIL_RESERVED_ROWS));
}

/** How far the open list reaches past its room, over the menu below it
 *  (a longer list ends in pb-3 and a 1px line). */
export function railOverflow(count: number): number {
  return count > RAIL_RESERVED_ROWS ? rowsHeight(count) + 13 - railReservedHeight(count) : 0;
}

/** The gap between the list's room and the menu (mt-11). */
const MENU_GAP_PX = 44;

/**
 * The drawer's switcher and the app's menu under it. The other apps' list
 * opens over the menu (client 2026-11-03), so while it is open the menu is
 * out of reach — no focus, no taps (`inert`) — and a tap on it, or Escape
 * wherever the focus is, closes the list. The menu is at least as tall as the
 * list reaches (Defensive Coordinator's is short), so the drawer never has to scroll
 * to show the list. Mounted with the drawer, so the list starts closed.
 */
function DrawerBody({
  header,
  children,
}: {
  header: Omit<Parameters<typeof AppSwitcherHeader>[0], "open" | "onOpenChange" | "arrowsRef">;
  children: ReactNode;
}) {
  const [appsOpen, setAppsOpen] = useState(false);
  const arrowsRef = useRef<HTMLButtonElement>(null);
  const others = header.apps.filter((app) => app.id !== header.currentAppId).length;
  useEffect(() => {
    if (!appsOpen) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setAppsOpen(false);
      arrowsRef.current?.focus();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [appsOpen]);
  const closeList = () => setAppsOpen(false);
  return (
    <>
      {/* Sigma exactly on the header bar's sigma — see drawerPosition. */}
      <div className="px-4 pt-[19px]">
        <AppSwitcherHeader {...header} open={appsOpen} onOpenChange={setAppsOpen} arrowsRef={arrowsRef} />
      </div>
      {/* In-game: the other apps' room, then this gap before the actions. */}
      <div className="relative mt-11">
        <div
          data-drawer-menu=""
          inert={appsOpen}
          className="flex flex-col gap-0.5 px-4 pb-3 text-left text-sm text-white"
          style={{ minHeight: Math.max(0, railOverflow(others) - MENU_GAP_PX) }}
        >
          {children}
        </div>
        {appsOpen && (
          <div
            aria-hidden
            data-drawer-menu-cover=""
            className="absolute inset-0"
            onClick={closeList}
            onTouchEnd={activateOnTap(closeList)}
          />
        )}
      </div>
    </>
  );
}

/**
 * Sidebar header, as the in-game AppSwitcherRail (client 2026-11-03): the
 * sigma with "Modern Football" and the app you are in stacked beside it, and
 * the up/down arrows that open the OTHER apps in the drawer itself, each with
 * its tile — the app you are in is already named, so it is not listed. Apps
 * the coach is subscribed to but can't open right now (offline, not opened
 * online yet) keep their row, dimmed, so the list never shifts. The list has
 * its own room under the header (railReservedHeight), empty while closed; a
 * longer one opens over the menu below instead of pushing it down, so the
 * drawer never grows (client 2026-11-03). With no other app it is a plain
 * label: no arrows.
 */
export function AppSwitcherHeader({
  currentAppId,
  currentAppName,
  apps,
  options,
  onSelect,
  open,
  onOpenChange,
  arrowsRef,
}: {
  currentAppId: string;
  currentAppName: string;
  /** The apps the coach is subscribed to (getSubscribedPracticeApps). */
  apps: readonly PracticeAppOption[];
  /** Those that can be opened right now (getPracticeAppOptions). */
  options: readonly PracticeAppOption[];
  onSelect: (id: string) => void;
  /** The other apps' list open: the drawer holds it (DrawerBody), as the
   *  list covers the drawer's menu. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The arrows, for the drawer to bring the focus back to. */
  arrowsRef?: Ref<HTMLButtonElement>;
}) {
  const others = apps.filter((app) => app.id !== currentAppId);
  const listId = useId();
  const canSwitch = others.length > 0;
  const toggle = () => onOpenChange(!open);
  return (
    <div className="flex flex-col">
      <button
        ref={arrowsRef}
        type="button"
        disabled={!canSwitch}
        aria-expanded={canSwitch ? open : undefined}
        aria-controls={canSwitch ? listId : undefined}
        onClick={toggle}
        onTouchEnd={canSwitch ? activateOnTap(toggle) : undefined}
        className="group flex w-full items-start gap-3 rounded-lg text-left focus:outline-none"
      >
        <span
          data-drawer-sigma=""
          className="relative flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center"
        >
          {Logo}
          {/* A new app version is waiting (same dot as the header sigma). */}
          <PendingUpdateDot className="-right-1 -top-1 ring-2 ring-[#0D121C]" />
        </span>
        <span className="-mt-[2px] min-w-0 flex-1">
          <span className="block truncate text-[15px] font-semibold leading-tight text-white">
            Modern Football
          </span>
          <span className="mt-0.5 block truncate text-xs leading-tight text-white/60">
            {currentAppName}
          </span>
        </span>
        {canSwitch && (
          <ChevronsUpDown
            aria-hidden
            className="h-4 w-4 flex-shrink-0 self-center text-white/60 transition group-hover:text-white group-focus-visible:text-white"
          />
        )}
      </button>
      <div
        // Always there, so the arrows' aria-controls never points at nothing.
        id={listId}
        className="relative mt-4"
        style={{ height: railReservedHeight(others.length) }}
      >
        {open && (
          <div
            role="group"
            aria-label="Other apps"
            // Over the menu below, on the drawer's own colour, ending in a line
            // where it covers part of it.
            className={cn(
              "absolute inset-x-0 top-0 z-10 flex flex-col gap-1 bg-[#0d121c]",
              others.length > RAIL_RESERVED_ROWS && "border-b border-white/10 pb-3"
            )}
          >
            {others.map((app) => {
              const available = options.some((o) => o.id === app.id);
              const select = () => onSelect(app.id);
              return (
                <button
                  key={app.id}
                  type="button"
                  aria-label={app.name}
                  disabled={!available}
                  onClick={select}
                  onTouchEnd={available ? activateOnTap(select) : undefined}
                  // 34px + px-2: the tile's left edge 42px in, under the "M"
                  // of Modern Football; the hover box a little wider.
                  className={cn(
                    "ml-[34px] flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition",
                    // The keyboard's place too: globals.css takes away every outline.
                    available ? "hover:bg-white/10 focus-visible:bg-white/10" : "opacity-30"
                  )}
                >
                  {/* Some files are taller than their tile (room for a shadow
                      below it): cropped from the bottom, so every tile is the
                      same 20px square. */}
                  <img
                    src={APP_ICONS[app.id]}
                    alt=""
                    className="h-5 w-5 flex-shrink-0 object-cover object-top"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium leading-tight text-white">
                    {app.name}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Drawer items (in-game SidebarOpen look) ──────────────────────────── */

const DrawerContext = createContext<{ close: () => void; isOnline: boolean }>({
  close: () => {},
  isOnline: true,
});

/**
 * One drawer row: icon in a 30px slot (lined up under the sigma), label, text
 * blue on hover and while selected. "action" is the in-game game/app item
 * (15px); "nav" is the slightly larger Sign Out row.
 */
export const sidebarItemClass = (
  selected = false,
  size: "action" | "nav" = "action"
): string =>
  cn(
    "flex cursor-pointer items-center gap-3 px-0 font-normal transition",
    size === "nav" ? "py-3 text-base" : "py-2 text-[15px]",
    selected ? "text-blue-600" : "text-white hover:text-blue-600"
  );

/** The icon size and slot every drawer row uses. */
export const SIDEBAR_ICON_CLASS = "h-5 w-[30px] flex-shrink-0";

const activateOnKey = (action: () => void) => (event: KeyboardEvent) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    action();
  }
};

/**
 * iOS Safari can swallow the first tap on a row with an icon inside a heavy
 * page (found in Special Teams). Handle the tap on touchend, drop the
 * synthetic mouse events (so it runs once), release focus. A scroll that
 * ends on a row is not a tap.
 */
const activateOnTap = (action: () => void) => (event: TouchEvent<HTMLElement>) => {
  if (!wasTouchTap()) return;
  event.preventDefault();
  action();
  event.currentTarget.blur();
};

export function SidebarItem({
  icon: Icon,
  label,
  onClick,
  selected = false,
  size = "action",
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  selected?: boolean;
  size?: "action" | "nav";
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected ? "page" : undefined}
      onClick={onClick}
      onKeyDown={activateOnKey(onClick)}
      onTouchEnd={activateOnTap(onClick)}
      className={sidebarItemClass(selected, size)}
    >
      <Icon className={SIDEBAR_ICON_CLASS} />
      <span>{label}</span>
    </div>
  );
}

/**
 * The in-game "LINE" between groups. `beforeSignOut`: the one above Sign Out,
 * whose taller row already adds 4px below — 11px there centres the line.
 */
export function SidebarDivider({ beforeSignOut = false }: { beforeSignOut?: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={beforeSignOut ? "mb-[11px] mt-4 h-px bg-white/10" : "my-4 h-px bg-white/10"}
    />
  );
}

/**
 * "Check For Updates", with the red dot while a new version is waiting. Works
 * offline too: an update already waiting needs no network to be taken. The
 * confirmation lives at the app root (CheckForUpdatesDialog); this closes the
 * drawer and asks for it.
 */
export function CheckForUpdatesItem() {
  const { close } = useContext(DrawerContext);
  const open = () => {
    close();
    openCheckForUpdates();
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={activateOnKey(open)}
      onTouchEnd={activateOnTap(open)}
      className={sidebarItemClass()}
    >
      <span className="relative inline-flex w-[30px] flex-shrink-0 justify-center">
        <RefreshCw className="h-5 w-5 flex-shrink-0" />
        <PendingUpdateDot className="-top-1 right-[3px] ring-2 ring-white" />
      </span>
      <span>Check For Updates</span>
    </div>
  );
}

/**
 * "Help and Support" (in-game parity, client 2026-10-08): its menu opens the
 * app's FAQ and Contact Us pages — pages like the others, under the app's own
 * nav bar (client 2026-10-12) — or the support chat (SupportCenter). Online
 * only, as in-game: all three need the server.
 */
export function HelpAndSupportItem({
  onFaq,
  onContactUs,
}: {
  /** Goes to this app's FAQ page. */
  onFaq: () => void;
  /** Goes to this app's Contact Us page. */
  onContactUs: () => void;
}) {
  const { close, isOnline } = useContext(DrawerContext);
  if (!isOnline) return null;
  const then = (go: () => void) => () => {
    close();
    go();
  };
  return (
    <HelpMenu
      className={sidebarItemClass()}
      onFaq={then(onFaq)}
      onContactUs={then(onContactUs)}
      onAskModern={then(() => openSupport("chat"))}
    >
      <CircleHelp className={SIDEBAR_ICON_CLASS} />
      <span>Help and Support</span>
    </HelpMenu>
  );
}

/* ── Where the drawer sits ────────────────────────────────────────────── */

const DRAWER_WIDTH = 264;
/** The drawer's own sigma, from its top-left corner: the header's padding
 *  (the sigma is the first box of a top-aligned row). */
const SIGMA_IN_DRAWER = { left: 16, top: 19 };
/** The app's centred box (AppShell) and the nav bar's inset in it. */
const APP_BOX_MAX_WIDTH = 1366;
const APP_INSET = 8;

/**
 * The drawer's corner, so its sigma lands exactly on the header bar's sigma
 * ([data-sigma-menu-button]) — wherever the app's centred box puts it, and
 * however far the page has scrolled. Without a header sigma (none on
 * screen): the top-left of the app's box, as the in-game drawer.
 */
export const drawerPosition = (
  sigma: { left: number; top: number } | null,
  viewportWidth: number
): { left: number; top: number } => {
  if (sigma) {
    return {
      left: Math.max(0, Math.round(sigma.left - SIGMA_IN_DRAWER.left)),
      top: Math.max(0, Math.round(sigma.top - SIGMA_IN_DRAWER.top)),
    };
  }
  return {
    left: Math.max(0, (viewportWidth - APP_BOX_MAX_WIDTH) / 2) + APP_INSET,
    top: APP_INSET,
  };
};

type DrawerPlace = { left: number; top: number; maxHeight: number; checked: boolean };

const headerSigmaRect = () =>
  document.querySelector("[data-sigma-menu-button]")?.getBoundingClientRect() ?? null;
const withHeight = (left: number, top: number, checked: boolean): DrawerPlace => ({
  left,
  top,
  maxHeight: Math.max(window.innerHeight - top - APP_INSET, 160),
  checked,
});
const measureDrawer = (): DrawerPlace => {
  const corner = drawerPosition(headerSigmaRect(), window.innerWidth);
  return withHeight(corner.left, corner.top, false);
};

/**
 * The drawer shell. The app puts its own actions (Library, Settings, Save
 * Game, Sign Out, dialogs, …) in `children`, already styled — this shell owns
 * position, size, colors and the switcher header, so every app looks
 * identical.
 */
export function LeftSidebar({
  isOpen,
  onClose,
  currentAppId,
  currentAppName,
  isOnline,
  version,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentAppId: string;
  currentAppName: string;
  isOnline: boolean;
  version?: string;
  children: ReactNode;
}) {
  const apps = useMemo(() => (isOpen ? getSubscribedPracticeApps() : []), [isOpen]);
  const options = useMemo(
    () => (isOpen ? getPracticeAppOptions(isOnline) : []),
    [isOpen, isOnline]
  );
  const [place, setPlace] = useState<DrawerPlace | null>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  // Measured before paint, and again when the window changes size.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const update = () => setPlace(measureDrawer());
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [isOpen]);
  // Then, still before paint, the drawer's own sigma is measured where it
  // actually rendered and the drawer nudged onto the header's — exact
  // whatever the layout does (fonts, zoom), not just as SIGMA_IN_DRAWER says.
  useLayoutEffect(() => {
    if (!place || place.checked) return;
    const header = headerSigmaRect();
    const panel = drawerRef.current;
    const own = panel?.querySelector("[data-drawer-sigma]")?.getBoundingClientRect();
    if (!header || !panel || !own) {
      setPlace({ ...place, checked: true });
      return;
    }
    // Where the sigma sits with the drawer scrolled to the top: on a short
    // window the coach may have scrolled it before a resize re-measures.
    setPlace(
      withHeight(
        Math.max(0, Math.round(place.left + header.left - (own.left + panel.scrollLeft))),
        Math.max(0, Math.round(place.top + header.top - (own.top + panel.scrollTop))),
        true
      )
    );
  }, [place]);
  const context = useMemo(() => ({ close: onClose, isOnline }), [onClose, isOnline]);

  if (!isOpen || typeof document === "undefined") return null;
  return createPortal(
    <DrawerContext.Provider value={context}>
      {/* Under the dialogs opened from the drawer (Save Game, Clear Playlist —
          dialogs sit at 99999, globals.css), as in-game, so none of them opens
          half-hidden behind it; above the page. The session-expired and
          sign-out overlays (125000 / 130000) stay above it all. */}
      <div className="fixed inset-0 z-[99990]">
        <div className="absolute inset-0" onClick={onClose} aria-label="Close sidebar" />
        <div
          ref={drawerRef}
          data-left-sidebar=""
          // A (non-modal) dialog: named for screen readers, and the "Update
          // Available!" prompt waits for it to close instead of opening
          // under it (isAnotherDialogOpen).
          role="dialog"
          aria-label="Menu"
          // As tall as its content, never the full screen; capped to the
          // viewport so it can still scroll on a short screen.
          className="absolute z-10 flex flex-col overflow-y-auto rounded bg-[#0d121c] shadow-lg"
          style={{
            width: DRAWER_WIDTH,
            left: place?.left ?? APP_INSET,
            top: place?.top ?? APP_INSET,
            maxHeight: place?.maxHeight,
            visibility: place?.checked ? undefined : "hidden",
          }}
        >
          <DrawerBody
            header={{
              currentAppId,
              currentAppName,
              apps,
              options,
              onSelect: (id) => {
                onClose();
                if (id !== currentAppId) void switchPracticeApp(id);
              },
            }}
          >
            {children}
          </DrawerBody>
          {version ? (
            <div className="flex items-center gap-2 px-4 pb-2 text-[10px] leading-none text-white">
              <span>Version {version}</span>
            </div>
          ) : null}
        </div>
      </div>
    </DrawerContext.Provider>,
    document.body
  );
}

export default LeftSidebar;
